import { useEffect, useMemo, useState } from 'react';
import { downloadCustodianIssuedCopy, listCustodianIssuedCopies } from '../../api/custodianApi';
import { downloadFileName } from '../../api/fileResponse';
import { CustodianFilePreview } from '../../components/custodian/CustodianFilePreview';
import { saveBlob } from '../../components/custodian/saveBlob';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { formatDateTime } from '../../utils/format';

export const CERTIFIED_TRUE_COPY_LABEL = 'Certified true copy — non-authoritative';

function displayCell(value) {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

export function CustodianCertifiedCopiesPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [previewId, setPreviewId] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await listCustodianIssuedCopies({ page: 1, pageSize: 50 });
        if (cancelled) return;
        const items = response?.items || [];
        setRows(items);
        setTotal(response?.meta?.total ?? items.length);
        setSelectedId((current) => current || items[0]?.id || '');
      } catch (requestError) {
        if (!cancelled) {
          setRows([]);
          setTotal(0);
          setError(requestError.message || 'Unable to load certified copies.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const selected = useMemo(
    () => rows.find((row) => row.id === selectedId) || rows[0] || null,
    [rows, selectedId]
  );
  const preview = rows.find((row) => row.id === previewId) || null;

  async function downloadSelected(row) {
    const target = row || selected;
    if (!target) return;
    setError('');
    try {
      const result = await downloadCustodianIssuedCopy(target.id);
      if (!result?.blob) {
        setError('Certified copy file could not be loaded.');
        return;
      }
      saveBlob(
        result.blob,
        downloadFileName({
          fileName: result.fileName,
          fallback: `certified-true-copy-${String(target.id).slice(0, 8)}`,
          mimeType: result.mimeType || result.blob.type,
        })
      );
    } catch (requestError) {
      setError(requestError.message || 'Unable to download certified copy.');
    }
  }

  return (
    <div className="page-stack">
      <header className="border-b border-evault-border pb-4">
        <h1 className="text-xl font-semibold text-evault-navy">Certified copy outputs</h1>
        <p className="mt-1 max-w-2xl text-xs text-evault-muted">
          Each file is labelled {CERTIFIED_TRUE_COPY_LABEL}. Authoritative means the
          sealed vault record. A certified true copy is still a copy.
          {loading ? ' Loading…' : error ? '' : ` ${total} issued.`}
        </p>
      </header>

      {error ? <ErrorBanner message={error} onRetry={() => setReloadToken((value) => value + 1)} /> : null}

      {!loading && !error && rows.length === 0 ? (
        <p className="text-sm text-evault-muted">No certified copies have been issued.</p>
      ) : !loading && !error ? (
        <section className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]">
          <div className="table-shell overflow-x-auto">
            <table className="min-w-full border-collapse text-left text-[11px]">
              <thead className="bg-evault-surface text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                <tr>
                  <th className="px-3 py-2">Copy</th>
                  <th className="px-3 py-2">Issued</th>
                  <th className="px-3 py-2">Source SHA-256</th>
                  <th className="px-3 py-2">Rendition SHA-256</th>
                  <th className="px-3 py-2">File</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`cursor-pointer border-t border-evault-border ${
                      selected?.id === row.id ? 'bg-evault-surface' : 'hover:bg-evault-surface/60'
                    }`}
                    onClick={() => setSelectedId(row.id)}
                  >
                    <td className="px-3 py-2 font-mono text-evault-navy">{row.id}</td>
                    <td className="px-3 py-2 text-evault-muted">{formatDateTime(row.generated_at)}</td>
                    <td className="max-w-[10rem] break-all px-3 py-2 font-mono text-evault-navy">
                      {displayCell(row.source_sha256)}
                    </td>
                    <td className="max-w-[10rem] break-all px-3 py-2 font-mono text-evault-navy">
                      {displayCell(row.rendition_sha256)}
                    </td>
                    <td className="px-3 py-2 text-evault-navy">{CERTIFIED_TRUE_COPY_LABEL}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <aside className="card-shell flex flex-col gap-3 p-4">
            {selected ? (
              <>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                    Selected copy
                  </p>
                  <p className="mt-1 text-sm font-semibold text-evault-navy">{CERTIFIED_TRUE_COPY_LABEL}</p>
                </div>
                <dl className="space-y-2 text-[11px]">
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                      Source fingerprint
                    </dt>
                    <dd className="mt-0.5 break-all font-mono text-evault-navy">
                      {displayCell(selected.source_sha256)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                      Rendition fingerprint
                    </dt>
                    <dd className="mt-0.5 break-all font-mono text-evault-navy">
                      {displayCell(selected.rendition_sha256)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                      Custodian
                    </dt>
                    <dd className="mt-0.5 text-evault-navy">{displayCell(selected.custodian_name)}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-evault-muted">
                      Custody status
                    </dt>
                    <dd className="mt-0.5 text-evault-navy">
                      {displayCell(selected.custody_status_as_of)}
                    </dd>
                  </div>
                </dl>
                <p className="text-[11px] text-evault-muted">
                  The source fingerprint is the sealed record. The rendition fingerprint is this
                  watermarked copy. They are not the same file.
                </p>
                <div className="flex flex-wrap gap-2 border-t border-evault-border pt-3">
                  <button
                    type="button"
                    className="rounded-full bg-evault-teal-deep px-3 py-1 text-[11px] font-semibold text-white"
                    onClick={() => downloadSelected(selected)}
                  >
                    Download
                  </button>
                  <button
                    type="button"
                    className="rounded-full border border-evault-border bg-white px-3 py-1 text-[11px] font-semibold text-evault-navy"
                    onClick={() => setPreviewId(selected.id)}
                  >
                    Preview
                  </button>
                </div>
              </>
            ) : (
              <p className="text-sm text-evault-muted">Select an issued copy.</p>
            )}
          </aside>
        </section>
      ) : null}

      {preview?.package_id && preview?.package_file_id ? (
        <section className="card-shell p-4 sm:p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-umb-neutral-dark">Labelled copy preview</h2>
            <button
              type="button"
              className="text-xs font-semibold text-evault-muted hover:text-umb-neutral-dark"
              onClick={() => setPreviewId('')}
            >
              Close preview
            </button>
          </div>
          <CustodianFilePreview
            key={`${preview.package_id}-${preview.package_file_id}-${preview.id}`}
            packageId={preview.package_id}
            fileId={preview.package_file_id}
            issuedCopyId={preview.id}
          />
        </section>
      ) : null}
    </div>
  );
}
